import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getExportFileErrorMessage, useExportScreeningFile } from '../../../api/created/exportFile.ts';
import { ScreeningCaseListItemDto } from '../../../api/created/screening.ts';
import { getScreeningExportMessage } from '../../../utils/screeningExportCode.ts';
import { SelectScreeningCase } from './SelectScreeningCase.tsx';

interface FormValues {
  screeningCase: ScreeningCaseListItemDto | null;
}

export function ExportScreeningDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      screeningCase: null,
    },
  });

  const screeningCaseValue = watch('screeningCase');
  const [exportErrorMessage, setExportErrorMessage] = useState<string | null>(null);

  const exportScreeningFileMutation = useExportScreeningFile();

  useEffect(() => {
    if (open) {
      reset({ screeningCase: null });
      setExportErrorMessage(null);
    }
  }, [open]);

  useEffect(() => {
    setExportErrorMessage(null);
  }, [screeningCaseValue]);

  const handleOnClose = () => {
    reset({ screeningCase: null });
    setExportErrorMessage(null);
    onClose();
  };

  const onSubmit = async ({ screeningCase }: FormValues) => {
    setExportErrorMessage(null);
    try {
      await exportScreeningFileMutation.mutateAsync(screeningCase!.screeningCaseId);
      handleOnClose();
    } catch (error) {
      setExportErrorMessage(await getExportFileErrorMessage(error, getScreeningExportMessage));
    }
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Screening exportieren</Typography>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <FormControl error={!!errors.screeningCase}>
              <FormLabel>
                <Typography>Screening-Auftrag *</Typography>
              </FormLabel>
              {/* Closed screening cases can be exported too, so active and closed ones are listed. */}
              <Controller
                name={'screeningCase'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <SelectScreeningCase value={value} onChange={onChange} enabled={open} />
                )}
              />
              {errors.screeningCase && <FormHelperText>Screening-Auftrag muss angegeben werden</FormHelperText>}
            </FormControl>

            {exportErrorMessage && (
              <Alert color={'danger'} startDecorator={<Icons.ErrorOutline />}>
                {exportErrorMessage}
              </Alert>
            )}

            <Divider inset="context" />
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button endDecorator={<Icons.Close />} color="neutral" onClick={handleOnClose}>
                Abbrechen
              </Button>
              <Button
                endDecorator={<Icons.Download />}
                type="submit"
                disabled={!screeningCaseValue}
                loading={exportScreeningFileMutation.isPending}>
                Exportieren
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
